import { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  Banner,
  Button,
  Flex,
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  Text,
} from '@electricjs/arc';

import { Employee } from '@/types/employees';
import { CopyButton } from '@common';
import { Asset } from '@/types/assets';
import { getDisplayFullName } from 'common/utils/getFullName';

const StyledModalBody = styled(ModalBody)`
  overflow-y: scroll;
`;

const StyledDeviceSelectedCount = styled(Text)`
  margin-top: 29px;
  font-weight: bold;
`;

const StyledTable = styled.table`
  margin: 10px 0;
  font-size: 1.6rem;
  border-collapse: separate;
  border-spacing: 0 13px;

  thead {
    text-align: left;
  }

  td:first-child {
    padding-right: 80px;
  }
`;

type MDMInviteModalProps = {
  visible: boolean;
  hide: () => void;
  employeesWithDevices: { employee: Employee; devices: Asset[] }[];
  isLoading: boolean;
  handleCancel: () => void;
  handleSubmit: () => void;
  onMarkNotApplicable?: (employeeId: string) => void;
  isMarkingNotApplicable?: boolean;
};

const MDMInviteModal = ({
  visible,
  hide,
  employeesWithDevices,
  isLoading,
  handleCancel,
  handleSubmit: originalHandleSubmit,
  onMarkNotApplicable,
  isMarkingNotApplicable,
}: MDMInviteModalProps) => {
  const { t } = useTranslation('billing');
  const { ithEnableNotApplicableMdmFlagForEmployees } = useFlags();
  const [hoveredEmployeeId, setHoveredEmployeeId] = useState<string | null>(
    null
  );

  const categorizeEmployeesByEmail = () => {
    const employeesWithEmail = employeesWithDevices.filter(
      employeeWithDevice => employeeWithDevice.employee.email?.length
    );
    const employeesWithoutEmail = employeesWithDevices.filter(
      employeeWithDevice => !employeeWithDevice.employee.email?.length
    );

    return { employeesWithEmail, employeesWithoutEmail };
  };

  const { employeesWithEmail, employeesWithoutEmail } =
    categorizeEmployeesByEmail();

  const selectedEmployeesWithEmails = employeesWithEmail;

  const handleSubmit = () => {
    originalHandleSubmit();
  };

  const downloadLink = window._env_?.VITE_DESKTOP_APP_DOWNLOAD_URL;

  const bannerIfEmployeeWithoutEmail = () => {
    if (employeesWithoutEmail.length === 0) {
      return null;
    }
    return (
      <Banner
        id="employees-without-email-banner"
        isFlat
        intent="warning"
        title={t('organization.mdmInviteModal.banner.title')}
        message={() => (
          <Text>
            {t('organization.mdmInviteModal.banner.message')}
            <br />
            <ul>
              {employeesWithoutEmail
                .map(e => e.employee)
                .map(employee => (
                  <li key={employee.id}>
                    <Text>{getDisplayFullName(employee)}</Text>
                  </li>
                ))}
            </ul>
          </Text>
        )}
      />
    );
  };

  const employeeDevicesFlattened = employeesWithDevices.flatMap(
    employeeWithDevices =>
      employeeWithDevices.devices.map<[Employee, Asset]>(device => [
        employeeWithDevices.employee,
        device,
      ])
  );

  return (
    <ModalV2 visible={visible} hide={hide} ariaLabelledby="org-mdm-invite">
      <ModalHeader>
        <Text variant="heading-2">
          {t('organization.mdmInviteModal.title')}
        </Text>
      </ModalHeader>
      <StyledModalBody>
        <Text>{t('organization.mdmInviteModal.description')}</Text>
        {bannerIfEmployeeWithoutEmail()}
        <StyledDeviceSelectedCount>
          {t('organization.mdmInviteModal.devicesSelected', {
            count: employeeDevicesFlattened.length,
          })}
        </StyledDeviceSelectedCount>
        <StyledTable>
          <thead>
            <tr>
              <th>
                <Text>{t('organization.mdmInviteModal.columns.device')}</Text>
              </th>
              <th>
                <Text>
                  {t('organization.mdmInviteModal.columns.deviceOwner')}
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {employeeDevicesFlattened.map(([employee, device]) => (
              <tr
                key={`${employee.id}-${device.id}`}
                onMouseEnter={() => setHoveredEmployeeId(employee.id)}
                onMouseLeave={() => setHoveredEmployeeId(null)}>
                <td>
                  <Text>
                    {device.name ??
                      device.device?.hardwareModel ??
                      device.device?.reportingData?.hostname}
                  </Text>
                </td>
                <td>
                  <Flex alignItems="center" justifyContent="space-between">
                    <Text>{getDisplayFullName(employee)}</Text>
                    {ithEnableNotApplicableMdmFlagForEmployees &&
                      onMarkNotApplicable &&
                      hoveredEmployeeId === employee.id && (
                        <Button
                          id={`not-applicable-employee-${employee.id}`}
                          variant="text"
                          size="small"
                          disabled={isMarkingNotApplicable}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            onMarkNotApplicable(employee.id);
                          }}>
                          {t(
                            'organization.mdmInviteModal.buttons.notApplicable'
                          )}
                        </Button>
                      )}
                  </Flex>
                </td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </StyledModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <ModalAction
            id="org-mdm-invite-invite-modal-cancel"
            variant="text"
            disabled={isLoading}
            onClick={handleCancel}>
            {t('organization.mdmInviteModal.buttons.cancel')}
          </ModalAction>
          <CopyButton
            id="copy-instructions-link-button"
            textToCopy={t('organization.mdmInviteModal.mdmInstructions', {
              downloadLink,
              interpolation: { escapeValue: false },
            })}>
            {t('organization.mdmInviteModal.buttons.copyInstructions')}
          </CopyButton>
          <ModalAction
            id="org-mdm-invite-invite-modal-submit"
            loading={isLoading}
            disabled={selectedEmployeesWithEmails.length === 0}
            onClick={handleSubmit}>
            {t('organization.mdmInviteModal.buttons.sendEmail')}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};

export default MDMInviteModal;
